import { Component, DestroyRef, Injector, Input, OnInit, Signal, computed, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { GlobalSettingsFormShape } from '../../components/smart-cart-form-global-settings/global-settings.model';
import {
  ButtonDesignFormGroup,
  CHECKOUT_AREA_STARTER_TEMPLATE,
  CHECKOUT_AREA_VARIABLES,
  CheckoutAreaFormShape,
} from './checkout-area.model';

interface LinkedButtonDesign {
  displayBackgroundColor: FormControl<string>;
  displayTextColor: FormControl<string>;
  displayHoverColor: FormControl<string>;
  displayCornerRadius: FormControl<number>;
  backgroundColorOverridden: Signal<boolean>;
  textColorOverridden: Signal<boolean>;
  hoverColorOverridden: Signal<boolean>;
  cornerRadiusOverridden: Signal<boolean>;
}

@Component({
  selector: 'smart-cart-form-checkout-area-editor',
  templateUrl: './checkout-area.component.html',
  styleUrls: ['./checkout-area.component.scss'],
  standalone: false,
})
export class SmartCartFormCheckoutAreaEditorComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<CheckoutAreaFormShape>;
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  @Input() usageToken = '';

  readonly starterTemplate = CHECKOUT_AREA_STARTER_TEMPLATE;
  readonly customCodeVariables = CHECKOUT_AREA_VARIABLES;

  readonly routingOptions: DropdownOption[] = [
    { label: 'Automatic', value: 'automatic' },
    { label: 'Custom URL', value: 'custom_url' },
  ];

  buttonsExpanded = true;
  checkoutMethodsExpanded = true;
  checkoutDesignExpanded = true;
  viewCartDesignExpanded = true;

  // Initialized eagerly so the template always has defined controls/signals to bind to;
  // ngOnInit only wires up the sync between widget controls and these display controls.
  readonly checkoutDesign: LinkedButtonDesign = this.buildEmptyLinkedDesign();
  readonly viewCartDesign: LinkedButtonDesign = this.buildEmptyLinkedDesign();

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    this.wireButtonDesign(this.checkoutDesign, this.formGroup.controls.checkoutButton.controls.design);
    this.wireButtonDesign(this.viewCartDesign, this.formGroup.controls.viewCartButton.controls.design);
  }

  private buildEmptyLinkedDesign(): LinkedButtonDesign {
    const placeholder = computed(() => false);
    return {
      displayBackgroundColor: new FormControl<string>('', { nonNullable: true }),
      displayTextColor: new FormControl<string>('', { nonNullable: true }),
      displayHoverColor: new FormControl<string>('', { nonNullable: true }),
      displayCornerRadius: new FormControl<number>(0, { nonNullable: true }),
      backgroundColorOverridden: placeholder,
      textColorOverridden: placeholder,
      hoverColorOverridden: placeholder,
      cornerRadiusOverridden: placeholder,
    };
  }

  toggleButtons(): void {
    this.buttonsExpanded = !this.buttonsExpanded;
  }

  toggleCheckoutMethods(): void {
    this.checkoutMethodsExpanded = !this.checkoutMethodsExpanded;
  }

  toggleCheckoutDesign(): void {
    this.checkoutDesignExpanded = !this.checkoutDesignExpanded;
  }

  toggleViewCartDesign(): void {
    this.viewCartDesignExpanded = !this.viewCartDesignExpanded;
  }

  resetToGlobal(widgetCtrl: AbstractControl): void {
    widgetCtrl.setValue(null);
  }

  onCheckoutButtonToggle(enabled: boolean): void {
    this.formGroup.controls.checkoutButton.controls.enabled.setValue(enabled);
    // Checkout and View Cart can't both be disabled — auto-enable the other.
    if (!enabled && !this.formGroup.controls.viewCartButton.controls.enabled.value) {
      this.formGroup.controls.viewCartButton.controls.enabled.setValue(true);
    }
  }

  onViewCartButtonToggle(enabled: boolean): void {
    this.formGroup.controls.viewCartButton.controls.enabled.setValue(enabled);
    if (!enabled && !this.formGroup.controls.checkoutButton.controls.enabled.value) {
      this.formGroup.controls.checkoutButton.controls.enabled.setValue(true);
    }
  }

  onTermsTextChange(html: string): void {
    this.formGroup.controls.terms.controls.text.setValue(html);
  }

  private wireButtonDesign(linked: LinkedButtonDesign, widgetDesign: ButtonDesignFormGroup): void {
    const widget = widgetDesign.controls;
    const global = this.globalSettingsFormGroup.controls.buttonDesign.controls;

    this.wireDisplayString(linked.displayBackgroundColor, widget.backgroundColor, global.backgroundColor);
    this.wireDisplayString(linked.displayTextColor, widget.textColor, global.textColor);
    this.wireDisplayString(linked.displayHoverColor, widget.hoverColor, global.hoverTextColor);
    this.wireDisplayNumber(linked.displayCornerRadius, widget.cornerRadius, global.cornerRadius);

    linked.backgroundColorOverridden = this.overriddenSignal(widget.backgroundColor);
    linked.textColorOverridden = this.overriddenSignal(widget.textColor);
    linked.hoverColorOverridden = this.overriddenSignal(widget.hoverColor);
    linked.cornerRadiusOverridden = this.overriddenSignal(widget.cornerRadius);
  }

  private wireDisplayString(
    display: FormControl<string>,
    widgetCtrl: FormControl<string | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as string), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      // Empty input ↔ inherit (null), so clearing reverts to the global fallback.
      const value = next || null;
      if (widgetCtrl.value !== value) widgetCtrl.setValue(value);
    });
  }

  private wireDisplayNumber(
    display: FormControl<number>,
    widgetCtrl: FormControl<number | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as number), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      if (widgetCtrl.value !== next) widgetCtrl.setValue(next);
    });
  }

  private overriddenSignal(widgetCtrl: AbstractControl): Signal<boolean> {
    const value = toSignal(widgetCtrl.valueChanges, {
      initialValue: widgetCtrl.value,
      injector: this.injector,
    });
    return computed(() => value() !== null);
  }
}
